import React from 'react';
import { Clock3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/number-input';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { buildCreditBasisOptions, getCreditBasisLabel, getCreditBasisDescription } from '../utils/creditBasisUtils';
import {
  TimelineBar,
  ViolationSpans,
  TimeAxis,
  computeZoneLayout,
  HATCH_BG,
  type TimelineZone,
  type ViolationSpan,
  type TimeAxisTick,
} from '@/components/config_forms/shared/help-graphics/timeline/TimelinePrimitives';
import {
  TIMELINE_ANNOTATION_INLINE_CLASS,
  TIMELINE_AXIS_LABEL_CLASS,
  TIMELINE_AXIS_TICK_CLASS,
  TIMELINE_CAPTION_TEXT_CLASS,
  TIMELINE_NOTE_TEXT_CLASS,
  TIMELINE_SEGMENT_HEADING_CLASS,
} from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';
import { ScenarioTogglePanel, type ScenarioOption } from '@/components/config_forms/shared/ScenarioTogglePanel';
import { ControlRow } from '@/components/config_forms/shared/ControlRow';
import { EquationValueBadge } from './EquationValueBadge';


/* ── Types ─────────────────────────────────────────────── */

interface TimelinePanel {
  id: string;
  title?: string;
  annotation?: string;
  zones: TimelineZone[];
  spans: ViolationSpan[];
  ticks: TimeAxisTick[];
}

/* ── Zone Content Helpers ─────────────────────────────── */

const SafeLabel: React.FC = () => (
  <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px]" style={{ color: 'color-mix(in srgb, var(--color-scenario-green), transparent 20%)' }}>
    Safe
  </span>
);

const CreditLabel: React.FC<{ text: string; color?: string }> = ({ text, color = 'color-mix(in srgb, var(--primary-text), transparent 8%)' }) => (
  <span className="font-mono text-[10px] sm:text-[12px] font-medium" style={{ color }}>{text}</span>
);

const BreakLabel: React.FC = () => (
  <span className="font-label text-[10px] sm:text-[12px] uppercase" style={{ color: 'color-mix(in srgb, var(--color-scenario-green), transparent 25%)' }}>
    Break
  </span>
);

const EllipsisContent: React.FC<{ variant?: 'primary' | 'destructive' }> = ({ variant = 'primary' }) => (
  <span className="text-[10px] sm:text-[12px] tracking-[2px]" style={{
    color: variant === 'destructive'
      ? 'color-mix(in srgb, var(--color-destructive), transparent 35%)'
      : 'color-mix(in srgb, var(--primary-text), transparent 35%)',
  }}>...</span>
);

/* ── Props ─────────────────────────────────────────────── */

export interface CreditExtraTimeTimelineProps {
  // Core config
  creditIntervalMinutes: number;
  timeCreditMinutes: number;
  deadlineHours: number;
  recurringDeadline: boolean;
  premiumBasis: string;
  mealTriggerMode: 'deadline' | 'count';
  requiredBreaks: number;
  ladder: Array<{ code: string; name: string; multiplier: number }>;

  // Method controls (write back to form state)
  onCreditIntervalChange?: (value: number | null) => void;
  onTimeCreditChange?: (value: number | null) => void;
  onPremiumBasisChange?: (basis: string) => void;

}

const WorkedHoursCallout: React.FC = () => (
  <div className={`mb-3 px-2 py-2 rounded-[0.25rem] border border-primary/10 bg-primary/[0.05] ${TIMELINE_NOTE_TEXT_CLASS}`}>
    <div className="flex items-start gap-2">
      <Clock3 className="h-3.5 w-3.5 shrink-0 mt-1 text-primary-text/90" aria-hidden="true" />
      <span>
        <strong className="text-primary-text/90 font-medium">Time Credits:</strong>{' '}
        The surcharge time credits do not add to your worked hours — they won't push you into a higher overtime tier.
      </span>
    </div>
  </div>
);

/* ── Panel Builders ────────────────────────────────────── */

function buildSingleViolationPanel(
  creditLabel: string,
  intervalLabel: string,
  deadlineLabel: string,
): TimelinePanel {
  const zones: TimelineZone[] = [
    { id: 'safe', variant: 'safe', flex: 3.5, content: <SafeLabel /> },
    { id: 'v1-1', variant: 'v1', flex: 2.5, content: <CreditLabel text={creditLabel} /> },
    { id: 'v1-2', variant: 'v1-alt', flex: 2.5, content: <CreditLabel text={creditLabel} /> },
    { id: 'ellipsis', variant: 'ellipsis', flex: 1.5, content: <EllipsisContent /> },
  ];
  const { lefts, widths } = computeZoneLayout(zones);
  return {
    id: 'single',
    zones,
    spans: [
      { id: 'violation', label: 'Violation', variant: 'v1', leftPct: lefts[1], rightPct: 0 },
    ],
    ticks: [
      { id: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
      { id: 'deadline', label: deadlineLabel, leftPct: lefts[1], align: 'center', variant: 'amber' },
      { id: 'int-1', label: intervalLabel, leftPct: lefts[2], align: 'center', variant: 'amber' },
      { id: 'int-2', label: intervalLabel, leftPct: lefts[3], align: 'center', variant: 'amber' },
    ],
  };
}

function buildStackingPanel(
  creditLabel: string,
  intervalLabel: string,
  deadlineLabel: string,
  deadlineHours: number,
): TimelinePanel {
  const zones: TimelineZone[] = [
    { id: 'safe', variant: 'safe', flex: 2.5, content: <SafeLabel /> },
    { id: 'v1-1', variant: 'v1', flex: 1, content: <CreditLabel text={creditLabel} /> },
    { id: 'v1-2', variant: 'v1-alt', flex: 1, content: <CreditLabel text={creditLabel} /> },
    { id: 'ellipsis-1', variant: 'ellipsis', flex: 1, borderRightOverride: 'border-r-2 border-r-scenario-green/30', content: <EllipsisContent /> },
    {
      id: 'overlap-1', variant: 'overlap', flex: 1,
      content: (
        <div className="flex flex-col items-center gap-0">
          <CreditLabel text={creditLabel} />
          <CreditLabel text={creditLabel} color="var(--destructive-text)" />
        </div>
      ),
    },
    {
      id: 'overlap-2', variant: 'overlap-alt', flex: 1,
      content: (
        <div className="flex flex-col items-center gap-0">
          <CreditLabel text={creditLabel} />
          <CreditLabel text={creditLabel} color="var(--destructive-text)" />
        </div>
      ),
    },
    { id: 'ellipsis-2', variant: 'ellipsis', flex: 1, bgOverride: HATCH_BG('color-mix(in srgb, var(--color-destructive), transparent 92%)'), content: <EllipsisContent variant="destructive" /> },
  ];
  const { lefts } = computeZoneLayout(zones);
  return {
    id: 'stacking',
    title: 'No Break Taken',
    annotation: 'Both violations earn credit simultaneously \u2014 2x credit per interval while both active.',
    zones,
    spans: [
      { id: 'v2', label: 'Violation 2', variant: 'v2', leftPct: lefts[4], rightPct: 0 },
      { id: 'v1', label: 'Violation 1', variant: 'v1', leftPct: lefts[1], rightPct: 0 },
    ],
    ticks: [
      { id: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
      { id: 'deadline-1', label: deadlineLabel, leftPct: lefts[1], align: 'center', variant: 'amber' },
      { id: 'int-1', label: intervalLabel, leftPct: lefts[2], align: 'center', variant: 'amber' },
      { id: 'int-2', label: intervalLabel, leftPct: lefts[3], align: 'center', variant: 'amber' },
      { id: 'deadline-2', label: `${deadlineHours * 2}h\ndeadline`, leftPct: lefts[4], align: 'center', variant: 'amber' },
      { id: 'int-3', label: intervalLabel, leftPct: lefts[5], align: 'center', variant: 'red' },
      { id: 'int-4', label: intervalLabel, leftPct: lefts[6], align: 'center', variant: 'red' },
    ],
  };
}

interface ResetPanelPair {
  id: 'reset';
  title: string;
  annotation: string;
  seg1: TimelinePanel;
  seg2: TimelinePanel;
}

function buildResetPanels(
  creditLabel: string,
  intervalLabel: string,
  deadlineLabel: string,
): ResetPanelPair {
  const seg1Zones: TimelineZone[] = [
    { id: 'safe', variant: 'safe', flex: 2.5, content: <SafeLabel /> },
    { id: 'v1-1', variant: 'v1', flex: 1.2, content: <CreditLabel text={creditLabel} /> },
    { id: 'v1-2', variant: 'v1-alt', flex: 1.2, content: <CreditLabel text={creditLabel} /> },
    { id: 'ellipsis', variant: 'ellipsis', flex: 1.1, content: <EllipsisContent /> },
    { id: 'break', variant: 'break', flex: 1.1, content: <BreakLabel /> },
  ];
  const seg1Layout = computeZoneLayout(seg1Zones);

  const seg2Zones: TimelineZone[] = [
    { id: 'safe', variant: 'safe', flex: 2.5, content: <SafeLabel /> },
    { id: 'v1-1', variant: 'v1', flex: 1.2, content: <CreditLabel text={creditLabel} /> },
    { id: 'v1-2', variant: 'v1-alt', flex: 1.2, content: <CreditLabel text={creditLabel} /> },
    { id: 'ellipsis', variant: 'ellipsis', flex: 1.1, content: <EllipsisContent /> },
  ];
  const seg2Layout = computeZoneLayout(seg2Zones);

  return {
    id: 'reset',
    title: 'Break Taken Between Violations',
    annotation: 'Break ends Violation 1, resets the deadline. Violation 2 starts fresh \u2014 no carry-over.',
    seg1: {
      id: 'reset-seg1',
      zones: seg1Zones,
      spans: [
        { id: 'v1', label: 'Violation 1', variant: 'v1', leftPct: seg1Layout.lefts[1], rightPct: seg1Layout.widths[4] },
      ],
      ticks: [
        { id: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
        { id: 'deadline', label: deadlineLabel, leftPct: seg1Layout.lefts[1], align: 'center', variant: 'amber' },
        { id: 'int-1', label: intervalLabel, leftPct: seg1Layout.lefts[2], align: 'center', variant: 'amber' },
        { id: 'int-2', label: intervalLabel, leftPct: seg1Layout.lefts[3], align: 'center', variant: 'amber' },
        { id: 'end', label: 'Break End', leftPct: 100, align: 'right', variant: 'label' },
      ],
    },
    seg2: {
      id: 'reset-seg2',
      zones: seg2Zones,
      spans: [
        { id: 'v2', label: 'Violation 2', variant: 'v1', leftPct: seg2Layout.lefts[1], rightPct: 0 },
      ],
      ticks: [
        { id: 'start', label: 'Break End', leftPct: 0, align: 'left', variant: 'label' },
        { id: 'deadline', label: deadlineLabel, leftPct: seg2Layout.lefts[1], align: 'center', variant: 'amber' },
        { id: 'int-1', label: intervalLabel, leftPct: seg2Layout.lefts[2], align: 'center', variant: 'amber' },
        { id: 'int-2', label: intervalLabel, leftPct: seg2Layout.lefts[3], align: 'center', variant: 'amber' },
        { id: 'end', label: 'End', leftPct: 100, align: 'right', variant: 'label' },
      ],
    },
  };
}

/* ── Utilities ────────────────────────────────────────── */

/* ── Rate Split Callout ────────────────────────────────── */

export function RateSplitCallout({ creditLabel, ladder, timeCreditMinutes, creditIntervalMinutes }: {
  creditLabel: string;
  ladder: Array<{ code: string; name: string; multiplier: number }>;
  timeCreditMinutes: number;
  creditIntervalMinutes: number;
}) {
  if (ladder.length < 2) return null;

  const low = ladder[0];
  const high = ladder[1];

  return (
    <div className="mt-3">
      <div className="max-w-[380px] mx-auto">
        {/* Bracket label */}
        <div className="text-center mb-2">
          <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px]"
            style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 20%)' }}>
            One credit interval
          </span>
        </div>
        {/* Split interval bar */}
        <div className="relative h-[52px] rounded-[4px] overflow-hidden"
          style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--primary), transparent 70%)' }}>
          <div className="absolute top-0 bottom-0 left-0 bg-primary/[0.06] flex items-center justify-center"
            style={{ width: '60%' }}>
            <span className="font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] font-semibold"
              style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 10%)' }}>
              {low.name}
            </span>
          </div>
          <div className="absolute top-0 bottom-0" style={{
            left: '60%', width: '2px',
            background: 'linear-gradient(to bottom, color-mix(in srgb, var(--color-foreground), transparent 70%), color-mix(in srgb, var(--color-foreground), transparent 90%))'
          }} />
          <div className="absolute top-0 bottom-0 right-0 bg-primary/[0.12] flex items-center justify-center"
            style={{ width: '40%' }}>
            <span className="font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] font-semibold"
              style={{ color: 'var(--primary-text)' }}>
              {high.name}
            </span>
          </div>
        </div>
        {/* Start/end labels */}
        <div className="flex justify-between mt-1">
          <span className={TIMELINE_AXIS_TICK_CLASS}>0m</span>
          <span className={TIMELINE_AXIS_TICK_CLASS}>{creditIntervalMinutes}m</span>
        </div>
        <div className={`mt-2 text-center ${TIMELINE_CAPTION_TEXT_CLASS}`}>
          The {timeCreditMinutes}m credit is split proportionally — each portion valued at its prevailing rate.
        </div>
      </div>
    </div>
  );
}

/* ── Partial Interval Callout ────────────────────────── */

export function PartialIntervalCallout({ creditLabel, creditIntervalMinutes }: {
  creditLabel: string;
  creditIntervalMinutes: number;
}) {
  const elapsedMinutes = Math.round(creditIntervalMinutes * 0.4);
  const remainingMinutes = Math.max(0, creditIntervalMinutes - elapsedMinutes);
  const elapsedPercent = creditIntervalMinutes > 0
    ? Math.min(100, Math.max(0, (elapsedMinutes / creditIntervalMinutes) * 100))
    : 0;
  const remainingPercent = Math.max(0, 100 - elapsedPercent);
  const dividerOffset = `${elapsedPercent}%`;
  const inBarStatusLabelClass =
    'font-sans text-[10px] sm:text-[11px] leading-none font-medium tracking-[0.2px] text-foreground/72';

  return (
    <div className="mt-3">
      <div className="max-w-[380px] mx-auto">
        {/* Bracket label */}
        <div className="text-center mb-2">
          <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px]"
            style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 20%)' }}>
            One credit interval
          </span>
        </div>
        {/* Interval bar: elapsed portion + break divider + remainder */}
        <div className="relative h-[52px] rounded-[4px] overflow-hidden"
          style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--primary), transparent 70%)' }}>
          {/* Credit label — centered in interval */}
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <span className="font-mono text-[10px] sm:text-[12px] font-semibold"
              style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 5%)' }}>
              {creditLabel}
            </span>
          </div>
          {/* Elapsed portion */}
          <div
            className="absolute top-0 bottom-0 left-0 bg-primary/[0.08] flex items-end px-2 pb-1"
            style={{ width: `${elapsedPercent}%` }}
          >
            <span className={inBarStatusLabelClass}>{`elapsed`}</span>
          </div>
          {/* Break divider */}
          <div
            className="absolute top-0 bottom-0 z-20"
            style={{ left: dividerOffset, width: '2px', background: 'var(--color-scenario-green)', transform: 'translateX(-1px)' }}
          />
          {/* Remainder — not reached */}
          <div
            className="absolute top-0 bottom-0 right-0 flex items-end justify-end px-2 pb-1"
            style={{
              width: `${remainingPercent}%`,
              background: 'repeating-linear-gradient(-55deg, color-mix(in srgb, var(--color-foreground), transparent 94%), color-mix(in srgb, var(--color-foreground), transparent 94%) 3px, transparent 3px, transparent 7px)',
            }}
          >
            <span className={inBarStatusLabelClass}>{`not reached`}</span>
          </div>
        </div>
        {/* Labels under bar */}
        <div className="relative mt-1 h-4">
          <span className={TIMELINE_AXIS_TICK_CLASS} style={{ position: 'absolute', left: 0, top: 0 }}>
            0m
          </span>
          <span className={TIMELINE_AXIS_TICK_CLASS} style={{ position: 'absolute', right: 0, top: 0 }}>
            {creditIntervalMinutes}m
          </span>
          <span
            className={TIMELINE_AXIS_LABEL_CLASS}
            style={{ position: 'absolute', left: dividerOffset, top: 0, transform: 'translateX(-50%)' }}
          >
            break taken
          </span>
        </div>
        <div className={`mt-2 text-center ${TIMELINE_CAPTION_TEXT_CLASS}`}>
          Full credit is applied even if the interval is cut short — partial intervals are not prorated.
        </div>
      </div>
    </div>
  );
}

/* ── Count Mode Sub-Components ────────────────────────── */

function CountModeEquationLine({ requiredBreaks, timeCreditMinutes, premiumBasis, ladder }: {
  requiredBreaks: number;
  timeCreditMinutes: number;
  premiumBasis: string;
  ladder: Array<{ code: string; name: string; multiplier: number }>;
}) {
  const effectiveBreaks = Math.max(1, requiredBreaks);
  const takenCount = effectiveBreaks <= 1 ? 0 : 1;
  const missedCount = effectiveBreaks - takenCount;

  const selectedBasisLabel = getCreditBasisLabel(premiumBasis, ladder);
  const description = getCreditBasisDescription(premiumBasis, selectedBasisLabel);

  return (
    <div className="w-full">
      <div className="w-full rounded-[0.25rem] border border-border/30 bg-surface-2/30 px-3 py-3">
        <div className="w-full flex flex-wrap items-start justify-center gap-x-1 gap-y-2 md:grid md:grid-cols-[max-content_minmax(16px,1fr)_max-content_minmax(16px,1fr)_max-content] md:justify-items-center md:items-start md:gap-x-2 md:gap-y-0">
          <EquationValueBadge className="min-w-[56px] md:min-w-0 md:w-full" label="Breaks Missed" value={`${missedCount}`} />
      <div className="mt-4 h-8 flex items-center justify-center font-mono text-[18px] leading-none text-foreground/85 md:justify-self-center" aria-hidden="true">
            ×
          </div>
          <EquationValueBadge className="min-w-[74px] md:min-w-0 md:w-full" label="Time Credit" value={`${timeCreditMinutes} min`} />
          <div className="mt-4 h-8 flex items-center justify-center font-mono text-[13px] leading-none font-medium tracking-[0.2px] text-foreground/85 whitespace-nowrap md:justify-self-center">
            at
          </div>
          <div className="min-w-[136px] text-center md:min-w-0 md:w-full">
            <div className="h-[12px] font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[1.1px] text-muted-foreground mb-1 flex items-center justify-center">
              Credit Basis
            </div>
            <div className="h-8 flex items-center justify-center text-[10px] sm:text-[12px] lg:text-[13px] font-medium text-muted-foreground whitespace-nowrap">{selectedBasisLabel}</div>
          </div>
        </div>
        <div className="mt-2 text-center text-[10px] sm:text-[12px] leading-relaxed">
          <div className="motion-safe:transition-colors motion-safe:duration-200 text-foreground">
            {description}
          </div>
        </div>
      </div>
    </div>
  );
}

function CountModeBasisRow({
  premiumBasis,
  ladder,
  onPremiumBasisChange,
}: {
  premiumBasis: string;
  ladder: Array<{ code: string; name: string; multiplier: number }>;
  onPremiumBasisChange?: (basis: string) => void;
}) {
  const hasLadderData = ladder.length > 0;
  const interactive = hasLadderData && !!onPremiumBasisChange;

  const basisOptions = buildCreditBasisOptions(ladder);
  const basisLabel = getCreditBasisLabel(premiumBasis, ladder);
  const description = getCreditBasisDescription(premiumBasis, basisLabel);

  return (
    <ControlRow label="Credit Basis" description={description}>
      {hasLadderData ? (
        <Select
          value={premiumBasis}
          onValueChange={interactive ? onPremiumBasisChange : undefined}
          disabled={!interactive}
        >
          <SelectTrigger aria-label="Credit basis" className="w-full md:w-auto md:min-w-[220px] min-h-[44px] input-recessed focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {basisOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="rounded border border-border/50 bg-muted/25 px-3 py-2 text-center text-[10px] sm:text-[12px] text-muted-foreground">
          Credit Basis options appear after an overtime ladder is configured.
        </div>
      )}
    </ControlRow>
  );
}

/* ── Main Component ────────────────────────────────────── */

export const CreditExtraTimeTimeline: React.FC<CreditExtraTimeTimelineProps> = (props) => {
  const { creditIntervalMinutes, timeCreditMinutes, deadlineHours, mealTriggerMode, recurringDeadline } = props;

  const creditLabel = `+${timeCreditMinutes}m`;
  const intervalLabel = `+${creditIntervalMinutes}m`;
  const deadlineLabel = `${deadlineHours}h\ndeadline`;
  const [recurringScenario, setRecurringScenario] = React.useState<'stacking' | 'reset'>('stacking');

  const singlePanel = React.useMemo(
    () => buildSingleViolationPanel(creditLabel, intervalLabel, deadlineLabel),
    [creditLabel, intervalLabel, deadlineLabel],
  );
  const stackingPanel = React.useMemo(
    () => buildStackingPanel(creditLabel, intervalLabel, deadlineLabel, deadlineHours),
    [creditLabel, intervalLabel, deadlineLabel, deadlineHours],
  );
  const resetPanels = React.useMemo(
    () => buildResetPanels(creditLabel, intervalLabel, deadlineLabel),
    [creditLabel, intervalLabel, deadlineLabel],
  );

  if (mealTriggerMode === 'count') {
    return (
      <div className="flex flex-col w-full">
        <CountModeEquationLine
          requiredBreaks={props.requiredBreaks}
          timeCreditMinutes={timeCreditMinutes}
          premiumBasis={props.premiumBasis}
          ladder={props.ladder}
        />
        <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
          {/* Time Credit control (always shown in count mode) */}
          {props.onTimeCreditChange && (
            <ControlRow label="Time Credit" description="Minutes of extra time credited per missed break as compensation.">
              <NumberInput
                aria-label="Time credit"
                size="xs"
                touchTarget
                value={timeCreditMinutes}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  props.onTimeCreditChange!(isNaN(val) ? null : val);
                }}
                unit="min"
                minValue={5}
                step={5}
              />
            </ControlRow>
          )}
          <CountModeBasisRow
            premiumBasis={props.premiumBasis}
            ladder={props.ladder}
            onPremiumBasisChange={props.onPremiumBasisChange}
          />
        </div>
      </div>
    );
  }

  const controls = (
    <div className="rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
      {mealTriggerMode === 'deadline' && props.onCreditIntervalChange && (
        <ControlRow label="Credit Interval" description="How often additional time credit accrues after the deadline is missed.">
          <NumberInput
            aria-label="Credit interval"
            size="xs"
            touchTarget
            value={creditIntervalMinutes}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              props.onCreditIntervalChange!(isNaN(val) ? null : val);
            }}
            unit="min"
            minValue={5}
            step={5}
          />
        </ControlRow>
      )}
      {props.onTimeCreditChange && (
        <ControlRow label="Time Credit" description="Minutes of extra time credited per interval as compensation for the missed break.">
          <NumberInput
            aria-label="Time credit"
            size="xs"
            touchTarget
            value={timeCreditMinutes}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              props.onTimeCreditChange!(isNaN(val) ? null : val);
            }}
            unit="min"
            minValue={5}
            step={5}
          />
        </ControlRow>
      )}
    </div>
  );

  const renderPanel = (panel: TimelinePanel) => (
    <div key={panel.id} className="w-full">
      {panel.title && (
        <div className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px] text-muted-foreground mb-2">
          {panel.title}
        </div>
      )}
      <ViolationSpans spans={panel.spans} />
      <TimelineBar zones={panel.zones} />
      <TimeAxis ticks={panel.ticks} />
      {panel.annotation && (
        <div className={TIMELINE_ANNOTATION_INLINE_CLASS}>
          {panel.annotation}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col w-full">
      {!recurringDeadline && renderPanel(singlePanel)}
      {recurringDeadline && (() => {
        const isReset = recurringScenario === 'reset';
        // Unified zone array — shared zones morph, scenario-specific zones collapse/expand
        const morphZones: TimelineZone[] = [
          { id: 'safe', variant: 'safe', flex: 2.5, content: <SafeLabel /> },
          { id: 'v1-1', variant: 'v1', flex: isReset ? 1.2 : 1, content: <CreditLabel text={creditLabel} /> },
          { id: 'v1-2', variant: 'v1-alt', flex: isReset ? 1.2 : 1, content: <CreditLabel text={creditLabel} /> },
          {
            id: 'ellipsis-1', variant: 'ellipsis', flex: isReset ? 0.6 : 1,
            borderRightOverride: isReset ? undefined : 'border-r-2 border-r-scenario-green/30',
            content: <EllipsisContent />,
          },
          {
            id: 'overlap-1', variant: 'overlap', flex: isReset ? 0 : 1,
            content: (
              <div className="flex flex-col items-center gap-0">
                <CreditLabel text={creditLabel} />
                <CreditLabel text={creditLabel} color="var(--destructive-text)" />
              </div>
            ),
          },
          {
            id: 'overlap-2', variant: 'overlap-alt', flex: isReset ? 0 : 1,
            content: (
              <div className="flex flex-col items-center gap-0">
                <CreditLabel text={creditLabel} />
                <CreditLabel text={creditLabel} color="var(--destructive-text)" />
              </div>
            ),
          },
          {
            id: 'ellipsis-2', variant: 'ellipsis', flex: isReset ? 0 : 1,
            bgOverride: HATCH_BG('color-mix(in srgb, var(--color-destructive), transparent 92%)'),
            content: <EllipsisContent variant="destructive" />,
          },
          { id: 'break', variant: 'break', flex: isReset ? 1.1 : 0, content: <BreakLabel /> },
        ];
        const { lefts, widths } = computeZoneLayout(morphZones);
        const morphSpans: ViolationSpan[] = isReset
          ? [{ id: 'v1', label: 'Violation 1', variant: 'v1', leftPct: lefts[1], rightPct: widths[7] }]
          : [
              { id: 'v2', label: 'Violation 2', variant: 'v2', leftPct: lefts[4], rightPct: 0 },
              { id: 'v1', label: 'Violation 1', variant: 'v1', leftPct: lefts[1], rightPct: 0 },
            ];
        const morphTicks: TimeAxisTick[] = [
          { id: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
          { id: 'deadline', label: deadlineLabel, leftPct: lefts[1], align: 'center', variant: 'amber' },
          { id: 'int-1', label: intervalLabel, leftPct: lefts[2], align: 'center', variant: 'amber' },
          { id: 'int-2', label: intervalLabel, leftPct: lefts[3], align: 'center', variant: 'amber' },
          { id: 'deadline-2', label: isReset ? '' : `${deadlineHours * 2}h\ndeadline`, leftPct: lefts[4], align: 'center', variant: 'amber' },
          { id: 'int-3', label: isReset ? '' : intervalLabel, leftPct: lefts[5], align: 'center', variant: 'red' },
          { id: 'int-4', label: isReset ? '' : intervalLabel, leftPct: lefts[6], align: 'center', variant: 'red' },
          { id: 'end', label: isReset ? 'Break End' : '', leftPct: 100, align: 'right', variant: 'label' },
        ];
        const morphAnnotation = isReset
          ? 'A meal break ends all active violations and starts a fresh work segment.'
          : 'With Recurring Deadline enabled, violations can overlap — each one runs from its trigger point until a meal break or shift end, incurring credits independently.';

        const scenarioOptions: ScenarioOption<'stacking' | 'reset'>[] = [
          { key: 'stacking', label: 'No Break Taken', symbol: '\u26A0', description: 'Violations overlap — each one runs from its trigger point, incurring credits independently' },
          { key: 'reset', label: 'Break Taken', symbol: '\u2713', description: 'A meal break ends all active violations and starts a fresh work segment' },
        ];

        return (
          <ScenarioTogglePanel<'stacking' | 'reset'>
            ariaLabel="Credit Accumulation Scenario"
            options={scenarioOptions}
            scenario={recurringScenario}
            onScenarioChange={setRecurringScenario}
            className="items-stretch w-full"
          >
            <div className="w-full">
              {isReset && (
                <div className={`${TIMELINE_SEGMENT_HEADING_CLASS} text-foreground/85 mb-1`}>
                  Work Segment 1
                </div>
              )}
              <ViolationSpans spans={morphSpans} />
              <TimelineBar zones={morphZones} />
              <TimeAxis ticks={morphTicks} />
              {morphAnnotation && (
                <div className={cn(TIMELINE_ANNOTATION_INLINE_CLASS, 'mb-0')}>
                  {morphAnnotation}
                </div>
              )}
            </div>

            <AnimatedCollapse open={isReset} duration={300} className="mt-4">
              <div className="w-full">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-px bg-scenario-green/15" />
                  <span className="font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] whitespace-nowrap text-scenario-green/85">
                    ↺ Clock resets
                  </span>
                  <div className="flex-1 h-px bg-scenario-green/15" />
                </div>

                <div className={`${TIMELINE_SEGMENT_HEADING_CLASS} text-foreground/85 mb-1`}>
                  Work Segment 2
                </div>
                <ViolationSpans spans={resetPanels.seg2.spans} />
                <TimelineBar zones={resetPanels.seg2.zones} />
                <TimeAxis ticks={resetPanels.seg2.ticks} />

                <div className={cn(TIMELINE_ANNOTATION_INLINE_CLASS, 'mb-0')}>
                  {resetPanels.annotation}
                </div>
              </div>
            </AnimatedCollapse>
          </ScenarioTogglePanel>
        );
      })()}
      <WorkedHoursCallout />
      {controls}

    </div>
  );
};
